import { useState } from "react";
import { ChevronDown } from "lucide-react";
import {
  PRODUCT_CAPABILITIES,
  TRIAL_DAYS,
  TRIAL_PLAN_NAME,
  getPublicPlan,
} from "@omnicanvas/core";
import { useMetaTags } from "../components/MetaTags";

const proPlan = getPublicPlan("pro");
const teamPlan = getPublicPlan("team");

const faqs = [
  {
    question: "Does OmniCanvas work offline?",
    answer:
      "Yes! OmniCanvas is offline-first. All your notes are stored locally on your device and available without an internet connection. When you're connected, changes sync automatically to the cloud.",
  },
  {
    question: "Who owns my data?",
    answer:
      "You do. Your notes and creations belong entirely to you. You can export your data at any time, and we will never sell or share your content with third parties.",
  },
  {
    question: "What platforms is OmniCanvas available on?",
    answer:
      "OmniCanvas is available as a macOS desktop app, a web app that works in any modern browser, and a mobile PWA you can install on your phone's home screen.",
  },
  {
    question: "Can I import from other note apps?",
    answer:
      "Yes! We currently support Evernote import (ENEX files) and Apple Notes import (from the macOS NoteStore.sqlite database). More import options coming soon including Notion and Markdown files.",
  },
  {
    question: "Is there a collaboration feature?",
    answer:
      `Yes! OmniCanvas supports real-time collaboration with live cursors, configurable permissions (view-only or edit), and email-based invite links. Share any note and work together simultaneously with up to ${PRODUCT_CAPABILITIES.maxCollaboratorsPerNote} collaborators.`,
  },
  {
    question: "Is OmniCanvas really free?",
    answer:
      `OmniCanvas has a generous Free plan with unlimited notes, cloud sync, encryption, version history, and every core canvas tool. Pro ($${proPlan.monthly}/month) adds meeting transcription, AI tools, semantic search, web clipping, API access, and a year of version history. Team starts at $${teamPlan.monthly}/seat/month for shared workspaces and central billing. Every new account gets ${TRIAL_DAYS} days of ${TRIAL_PLAN_NAME} access — no credit card required.`,
  },
  {
    question: "How do backups and version history work?",
    answer:
      "OmniCanvas automatically snapshots your notes every time they sync. For the last 72 hours you get continuous snapshots (about every 30 seconds during active editing). After that, snapshots are consolidated into daily versions. Free keeps 3 daily snapshots per note; paid plans keep 365. Every note always retains at least 3 snapshots regardless of age, and paid users can save up to 10 manual checkpoints per note that are never automatically deleted.",
  },
  {
    question: "How is my data stored?",
    answer:
      "Your data is stored locally on your device by default, and you can use OmniCanvas entirely offline. If you enable cloud sync, your data is encrypted in transit and at rest on our servers (powered by Cloudflare). For sensitive notes, you can also turn on end-to-end, zero-knowledge encryption — enabled per note or per folder. Once you enable it, that content is encrypted on your device before it syncs, so not even OmniCanvas can read it. (It's opt-in because encrypted notes are excluded from server-side features like full-text search.)",
  },
  {
    question: "Can I use OmniCanvas for drawings?",
    answer:
      "Absolutely! OmniCanvas includes freehand drawing, shapes, arrows, text, and more on an infinite canvas. Apple Pencil and other pointer styluses work in the web app and installed PWA; OmniCanvas is a spatial canvas rather than a lined-paper handwriting replacement.",
  },
  {
    question: "Does OmniCanvas support two-factor authentication?",
    answer:
      "Yes. You can enable TOTP-based two-factor authentication in Settings. OmniCanvas works with any authenticator app including Google Authenticator, Authy, and 1Password.",
  },
  {
    question: "What AI features does OmniCanvas have?",
    answer:
      "OmniCanvas Pro and Team include audio transcription, handwriting OCR, semantic search, AI folder organization, and transcript summarization. These features help you capture and find information faster.",
  },
];

export function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useMetaTags({
    title: "Frequently Asked Questions | OmniCanvas",
    description: "Answers to common questions about OmniCanvas — offline support, data ownership, pricing, platforms, collaboration, AI features, and more.",
    url: "https://omnicanvasnotes.com/faq",
  });

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <div className="pt-28 pb-20">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <div className="max-w-2xl mx-auto px-6">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-extrabold tracking-tight text-slate-900 mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-lg text-slate-600">
            Everything you need to know about OmniCanvas.
          </p>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border border-slate-200 rounded-xl overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-slate-50 transition-colors"
              >
                <span className="text-sm font-semibold text-slate-900 pr-4">
                  {faq.question}
                </span>
                <ChevronDown
                  size={18}
                  className={`flex-shrink-0 text-slate-400 transition-transform duration-200 ${
                    openIndex === index ? "rotate-180" : ""
                  }`}
                />
              </button>
              {openIndex === index && (
                <div className="px-6 pb-4">
                  <p className="text-sm text-slate-600 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
